import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getMatchingTemplates } from "../services/GalleryContentService";
import { MerchandisingProductView } from "../types/ContentfulTypes";
import { ProductContext } from "./ProductContextProvider";
import { isEqual } from "lodash";
import { getTemplateId, isTemplateFeatureFlag } from "../utils/ReferentialImageryHelper";
import PageConfig from "../utils/PageConfig";
import { useSnackbarContext } from "./SnackbarContext";
import { MpvProvider } from "../utils/MpvProvider";
import { getQueryParametersFromUrl } from "../utils/urlHelper";
import { Logger } from "../logging/logger";
import { getPreview } from "../services/lookupPreviewService";

const PRODUCT_AUDIENCE_PARAMETER = "productAudience";
const KEYWORDS_PARAMETER = "keywords";

export interface FeedTemplateData {
  templateInformation: TemplateInformation;
  productAudience?: string;
  keywords?: string;
}

export const FeedTemplateContext = createContext<FeedTemplateData | undefined>(undefined);

export interface GalleryTemplateContextProps {
  locale: string;
  mpv: MerchandisingProductView;
}

export const FeedTemplateProvider = (props: PropsWithChildren<GalleryTemplateContextProps>) => {
  const { locale, mpv } = props;
  const { selections } = useContext(ProductContext);
  const [templateUrls, setTemplateUrls] = useState<TemplateInformation | undefined>(undefined);
  const [productAudience, setProductAudience] = useState<string | undefined>(undefined);
  const [keywords, setKeywords] = useState<string | undefined>(undefined);
  const { showSnackbarMessage } = useSnackbarContext();

  const configDeepCompareMemoize = (value: any) => {
    const ref = useRef(value);

    if (!isEqual(value, ref.current)) {
      ref.current = value;
    }

    return ref.current;
  };

  const allQueryStringParameters = getQueryParametersFromUrl();

  const queryParamsProductAttributes = useMemo(() => {
    try {
      const mpvProvider = new MpvProvider(mpv);
      const productAttributes = Object.keys(mpvProvider.getAllOptionsAndValues());

      return Object.keys(allQueryStringParameters)
        .filter(key => productAttributes.includes(key))
        .reduce((attributes: Record<string, string>, key) => {
          attributes[key] = allQueryStringParameters[key];
          return attributes;
        }, {});
    } catch (e) {
      Logger.instance.error("Cannot get product attributes from query parameters", {
        mpvId: mpv?.mpvId,
        productKey: mpv?.coreProductId,
        locale,
        selections,
      });
      return null;
    }
  }, [mpv]);

  useEffect(() => {
    if (!isTemplateFeatureFlag()) return;
    Logger.instance.info(`IsTemplateFeatureFlag enabled`, {
      mpvId: mpv.mpvId,
      productKey: mpv.coreProductId,
      locale,
      selections,
    });
    setupUrlTemplates();
  }, [mpv.mpvId, locale, configDeepCompareMemoize(selections), queryParamsProductAttributes]);

  async function setupUrlTemplates() {
    if (!selections || !queryParamsProductAttributes) return;

    const templateId = getTemplateId();
    if (!templateId) {
      setTemplateUrls(undefined);
      return;
    }
    const completeSelection = {
      ...queryParamsProductAttributes,
      ...selections,
    };

    const compatibleTemplateUrls = await getMatchingTemplates(
      mpv.coreProductId,
      mpv.version,
      templateId,
      locale,
      completeSelection,
      PageConfig.requestor
    );
    Logger.instance.info(`Matching templates: ${compatibleTemplateUrls?.length || 0}`, {
      mpvId: mpv.mpvId,
      productKey: mpv.coreProductId,
      locale,
      selections,
      completeSelection,
      templateId,
    });

    if (compatibleTemplateUrls && compatibleTemplateUrls.length === 1) {
      const compatibleTemplateUrl = compatibleTemplateUrls[0];

      const previews = await getPreview(
        compatibleTemplateUrl.instructionsUrl,
        mpv.coreProductId,
        mpv.version,
        compatibleTemplateUrl.fullProductSpecification,
        locale
      );

      if (previews?.views?.length) {
        const urlViewToShow = (
          previews.views.find(view => view.name === "front") || previews.views[0]
        )?._links?.image?.href;

        if (urlViewToShow) {
          setTemplateUrls({
            studioUrl: compatibleTemplateUrl.studioUrl,
            altText: compatibleTemplateUrl.altText,
            previewUrl: urlViewToShow,
          });
          if (allQueryStringParameters[PRODUCT_AUDIENCE_PARAMETER]) {
            setProductAudience(allQueryStringParameters[PRODUCT_AUDIENCE_PARAMETER]);
          }
          if (allQueryStringParameters[KEYWORDS_PARAMETER]) {
            setKeywords(allQueryStringParameters[KEYWORDS_PARAMETER]);
          }
          return;
        }
      }
      invalidateDocument("previewUrl from lookup");
    } else invalidateDocument("gallery matching templates");

    function invalidateDocument(invalidResponseField: string) {
      Logger.instance.info(`Could not get ${invalidResponseField} for new selection`, {
        mpvId: mpv.mpvId,
        productKey: mpv.coreProductId,
        locale,
        selections,
        completeSelection,
        templateId,
        invalidResponseField,
      });
      if (templateUrls) {
        showSnackbarMessage("The template ID is not valid for the current selections", "warning");
        setTemplateUrls(undefined);
      }
    }
  }

  return (
    <FeedTemplateContext.Provider
      value={
        templateUrls ? { templateInformation: templateUrls, productAudience, keywords } : undefined
      }
    >
      {props.children}
    </FeedTemplateContext.Provider>
  );
};

interface TemplateInformation {
  studioUrl: string;
  previewUrl: string;
  altText?: string;
}
